import configs from "../configs.js";
import { getAuthHeaders } from "../ui/utils.js";

/**
 * Pure API client for Persistence API that automatically handles workspace_id and authentication tokens.
 * This client should be configured with a workspaceIdGetter function from the AuthContext.
 */
export default class PersistenceApiClient {
  constructor() {
    this.workspaceIdGetter = null;
    this.defaultWorkspaceIdGetter = null;
    this.baseUrl = configs.PERSISTENCE_API_URL;
    this.reauthHandler = null;
    this.getAuthHeaders = getAuthHeaders;
  }

  setReauthHandler(handler) {
    this.reauthHandler = typeof handler === "function" ? handler : null;
  }

  isReauthCanaryEnabled() {
    // Default-on rollout: only disable explicitly with auth_canary_dashboard_reauth=0
    return localStorage.getItem("auth_canary_dashboard_reauth") !== "0";
  }

  async executeWithAuthRetry(execute, meta = {}) {
    let response = await execute();

    const shouldRetryWithReauth =
      response.status === 401 && this.isReauthCanaryEnabled() && typeof this.reauthHandler === "function";

    if (!shouldRetryWithReauth) {
      return response;
    }

    const didReauthenticate = await this.reauthHandler(meta).catch(() => false);

    if (!didReauthenticate) {
      return response;
    }

    response = await execute();
    return response;
  }

  /**
   * Set a function to get the current workspace ID from context.
   * This should be called from components that have access to AuthContext.
   * @param {Function} getter - Function that returns the current workspace ID
   */
  setWorkspaceIdGetter(getter) {
    this.workspaceIdGetter = getter;
  }

  /**
   * Set a function to get the user's default workspace ID from context.
   * This is used for avatar operations that should always use the default workspace.
   * @param {Function} getter - Function that returns the default workspace ID
   */
  setDefaultWorkspaceIdGetter(getter) {
    this.defaultWorkspaceIdGetter = getter;
  }

  /**
   * Get the user's default workspace ID (for avatar operations).
   * @returns {string|null} The default workspace ID or null if not available
   */
  getDefaultWorkspaceId() {
    if (this.defaultWorkspaceIdGetter) {
      return this.defaultWorkspaceIdGetter();
    }
    // Fallback to current workspace if no default getter is set
    return this.getWorkspaceId();
  }

  /**
   * Get the current workspace ID from context or localStorage.
   * @returns {string|null} The workspace ID or null if not available
   */
  getWorkspaceId() {
    // Try to get workspace_id from context first (via injected getter)
    if (this.workspaceIdGetter) {
      const workspaceId = this.workspaceIdGetter();
      if (workspaceId) {
        return workspaceId;
      }
    }

    // Fallback to localStorage (for backward compatibility during transition)
    const workspaceId = localStorage.getItem("selected_workspace_id");
    return workspaceId;
  }

  async getRequestAuthHeaders(url, method) {
    return await this.getAuthHeaders(url, method);
  }

  /**
   * Build a URL with workspace prefix for workspace-scoped resources.
   * @param {string} path - The resource path (e.g., '/assets-view', '/assets/123')
   * @returns {string} The full URL with workspace prefix
   */
  buildWorkspaceUrl(path) {
    const workspaceId = this.getWorkspaceId();
    if (!workspaceId) {
      throw new Error("No workspace selected. Please select a workspace first.");
    }
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${this.baseUrl}/workspaces/${workspaceId}/${cleanPath}`;
  }

  /**
   * Build a URL for non-workspace resources.
   * @param {string} path - The resource path (e.g., '/assets', '/assets-view')
   * @returns {string} The full URL
   */
  buildUrl(path) {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${this.baseUrl}/${cleanPath}`;
  }

  /**
   * Make an authenticated API request with automatic workspace handling.
   * @param {string} url - The full URL or path
   * @param {Object} options - Fetch options (method, body, headers, etc.)
   * @param {boolean} useWorkspace - Whether to prefix the URL with workspace (default: false)
   * @returns {Promise<Response>} The fetch response
   */
  async request(url, options = {}, useWorkspace = false) {
    // Build the full URL
    const fullUrl = useWorkspace ? this.buildWorkspaceUrl(url) : this.buildUrl(url);

    const execute = async () => {
      const method = options.method || "GET";
      const authHeaders = await this.getRequestAuthHeaders(fullUrl, method);

      // Merge headers
      const headers = {
        "Content-Type": "application/json",
        ...authHeaders,
        Accept: "application/json",
        ...options.headers
      };

      // Remove headers explicitly set to null or undefined
      Object.keys(headers).forEach(key => {
        if (headers[key] === undefined || headers[key] === null) {
          delete headers[key];
        }
      });

      return fetch(fullUrl, {
        ...options,
        headers
      });
    };

    return this.executeWithAuthRetry(execute, { path: url, useWorkspace, client: "persistence" });
  }

  /**
   * GET request helper.
   * @param {string} path - The resource path
   * @param {Object} queryParams - Optional query parameters
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @param {AbortSignal} signal - Optional abort signal
   * @returns {Promise<Response>} The fetch response
   */
  async get(path, queryParams = {}, useWorkspace = false, signal = null) {
    const params = new URLSearchParams(this.sanitizeQueryParams(queryParams));
    const url = params.toString() ? `${path}?${params.toString()}` : path;
    return this.request(url, { method: "GET", signal }, useWorkspace);
  }

  sanitizeQueryParams(queryParams = {}) {
    return Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === "") {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});
  }

  /**
   * POST request helper.
   * @param {string} path - The resource path
   * @param {Object} body - The request body (will be JSON stringified)
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Response>} The fetch response
   */
  async post(path, body = {}, useWorkspace = false, requestOptions = {}) {
    return this.request(
      path,
      {
        ...requestOptions,
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...(requestOptions.headers || {})
        }
      },
      useWorkspace
    );
  }

  /**
   * PATCH request helper.
   * @param {string} path - The resource path
   * @param {Object} body - The request body (will be JSON stringified)
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Response>} The fetch response
   */
  async patch(path, body = {}, useWorkspace = false) {
    return this.request(
      path,
      {
        method: "PATCH",
        body: JSON.stringify(body)
      },
      useWorkspace
    );
  }

  /**
   * DELETE request helper.
   * @param {string} path - The resource path
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Response>} The fetch response
   */
  async delete(path, useWorkspace = false) {
    return this.request(path, { method: "DELETE" }, useWorkspace);
  }

  /**
   * Handle response and parse JSON with error handling.
   * @param {Response} response - The fetch response
   * @returns {Promise<Object>} The parsed JSON data
   * @throws {Error} If the response is not ok
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} ${errorText}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }

    return await response.text();
  }

  /**
   * Convenience method: GET request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} queryParams - Optional query parameters
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @param {AbortSignal} signal - Optional abort signal
   * @returns {Promise<Object>} The parsed JSON response
   */
  async getJson(path, queryParams = {}, useWorkspace = false, signal = null) {
    const response = await this.get(path, queryParams, useWorkspace, signal);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: POST request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} body - The request body
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async postJson(path, body = {}, useWorkspace = false, requestOptions = {}) {
    const response = await this.post(path, body, useWorkspace, requestOptions);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: PATCH request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} body - The request body
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async patchJson(path, body = {}, useWorkspace = false) {
    const response = await this.patch(path, body, useWorkspace);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: DELETE request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: false)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async deleteJson(path, useWorkspace = false) {
    const response = await this.delete(path, useWorkspace);
    return this.handleResponse(response);
  }

  /**
   * Make a public (unauthenticated) GET request with automatic JSON parsing.
   * Use this for public endpoints that don't require authentication.
   * @param {string} path - The resource path
   * @param {Object} queryParams - Optional query parameters
   * @returns {Promise<Object>} The parsed JSON response
   */
  async getPublicJson(path, queryParams = {}) {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    const params = new URLSearchParams(this.sanitizeQueryParams(queryParams));
    const url = params.toString()
      ? `${this.baseUrl}/${cleanPath}?${params.toString()}`
      : `${this.baseUrl}/${cleanPath}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" }
    });

    return this.handleResponse(response);
  }

  /**
   * Fetch a single avatar by ID or SID (public endpoint, no auth required).
   * @param {string} idOrSid - The avatar ID or SID
   * @returns {Promise<Object>} The avatar data
   */
  async getAvatarById(idOrSid) {
    return this.getPublicJson(`avatars/${idOrSid}`);
  }

  /**
   * List all avatars for the authenticated user (Global).
   * @param {Object} params - Query parameters (page, per_page, search, is_featured)
   * @returns {Promise<Object>} The paginated avatars response
   */
  async getAvatars(params = {}) {
    return this.getJson("avatars", params);
  }

  /**
   * List avatars for the standalone admin management page.
   * This is intentionally non-workspace scoped.
   * @param {Object} params
   * @returns {Promise<Object>}
   */
  async getManagedAvatars(params = {}) {
    return this.getJson("avatars", params);
  }

  /**
   * Fetch the public active platform-pick avatar from the compatibility endpoint.
   * @returns {Promise<Object>}
   */
  async getDefaultAvatar() {
    return this.getPublicJson("avatars/default");
  }

  /**
   * Update an avatar through the global management endpoint.
   * @param {string|number} avatarId
   * @param {Object} body
   * @returns {Promise<Object>}
   */
  async updateManagedAvatar(avatarId, body = {}) {
    return this.patchJson(`avatars/${avatarId}`, body);
  }

  /**
   * Delete an avatar through the global management endpoint.
   * @param {string|number} avatarId
   * @returns {Promise<Object>}
   */
  async deleteManagedAvatar(avatarId) {
    return this.deleteJson(`avatars/${avatarId}`);
  }

  /**
   * Promote a user avatar into a platform-owned system avatar.
   * @param {string|number} avatarId
   * @returns {Promise<Object>}
   */
  async promoteManagedAvatar(avatarId) {
    return this.postJson(`avatars/${avatarId}/promote`, {});
  }

  /**
   * List all avatars in the current workspace.
   * @param {Object} params - Query parameters (page, per_page, search, is_featured)
   * @returns {Promise<Object>} The paginated avatars response
   */
  async getWorkspaceAvatars(params = {}) {
    return this.getJson("avatars", params, true);
  }

  /**
   * Build a URL with the user's default workspace prefix.
   * Used for avatar operations that should always use the default workspace.
   * @param {string} path - The resource path
   * @returns {string} The full URL with default workspace prefix
   */
  buildDefaultWorkspaceUrl(path) {
    const defaultWorkspaceId = this.getDefaultWorkspaceId();
    if (!defaultWorkspaceId) {
      throw new Error("No default workspace available. Please sign in first.");
    }
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${this.baseUrl}/workspaces/${defaultWorkspaceId}/${cleanPath}`;
  }

  /**
   * List all avatars in the user's default workspace.
   * This should be used for "My Avatars" to ensure avatars are always fetched from the default workspace.
   * @param {Object} params - Query parameters (page, per_page, search, is_featured)
   * @returns {Promise<Object>} The paginated avatars response
   */
  async getDefaultWorkspaceAvatars(params = {}) {
    const queryString = new URLSearchParams(this.sanitizeQueryParams(params)).toString();
    const path = `avatars${queryString ? `?${queryString}` : ""}`;
    const url = this.buildDefaultWorkspaceUrl(path);
    const execute = async () => {
      const authHeaders = await this.getRequestAuthHeaders(url, "GET");
      return fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          Accept: "application/json"
        }
      });
    };
    const response = await this.executeWithAuthRetry(execute, {
      path,
      useWorkspace: true,
      scope: "default-workspace",
      client: "persistence"
    });
    return this.handleResponse(response);
  }

  /**
   * Make an authenticated request to the default workspace (for avatar PATCH/DELETE operations).
   * @param {string} method - The HTTP method (PATCH, DELETE)
   * @param {string} path - The resource path
   * @param {Object} body - Optional request body
   * @returns {Promise<Object>} The parsed JSON response
   */
  async requestDefaultWorkspace(method, path, body = null) {
    const url = this.buildDefaultWorkspaceUrl(path);
    const execute = async () => {
      const authHeaders = await this.getRequestAuthHeaders(url, method);
      const options = {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          Accept: "application/json"
        }
      };
      if (body) {
        options.body = JSON.stringify(body);
      }
      return fetch(url, options);
    };
    const response = await this.executeWithAuthRetry(execute, {
      path,
      method,
      useWorkspace: true,
      scope: "default-workspace",
      client: "persistence"
    });
    return this.handleResponse(response);
  }
}

// Export a singleton instance
export const persistenceApiClient = new PersistenceApiClient();
